import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, AlertCircle, Loader2, ShieldCheck, Mail, KeyRound, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { API } from "../apiBase";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function MssanteConnexion() {
  const { setSession, user } = useProAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Allow ?redirect=/studio (or any other internal path) to drive post-login navigation.
  // We accept ONLY internal paths starting with a single "/" to avoid open-redirect attacks.
  const getRedirectTarget = () => {
    const sp = new URLSearchParams(location.search);
    const r = sp.get("redirect") || "";
    if (r.startsWith("/") && !r.startsWith("//") && !r.includes(":")) return r;
    return null;
  };
  const redirectTarget = getRedirectTarget();
  const [mode, setMode] = useState("password"); // password | code
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsRef = useRef(null);
  const tsId = useRef(null);

  // 2FA challenge state
  const [twofa, setTwofa] = useState(null); // { challenge_token, ttl } or null
  const [twofaCode, setTwofaCode] = useState("");

  useEffect(() => {
    if (user) navigate(redirectTarget || "/mssante/bienvenue", { replace: true });
  }, [user, navigate, redirectTarget]);

  useEffect(() => {
    if (mode !== "code" || !TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = "cf-turnstile-script";
    const render = () => {
      if (!window.turnstile || !tsRef.current) return;
      try { if (tsId.current) window.turnstile.remove(tsId.current); }
      catch (e) { console.debug("ts remove:", e); }
      tsId.current = window.turnstile.render(tsRef.current, {
        sitekey: TURNSTILE_SITE_KEY, theme: "dark",
        callback: (t) => setTsToken(t),
        "error-callback": () => setTsToken(""),
        "expired-callback": () => setTsToken(""),
      });
    };
    if (window.turnstile) { render(); return; }
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true; s.defer = true; s.onload = render;
      document.head.appendChild(s);
    } else {
      const i = setInterval(() => { if (window.turnstile) { clearInterval(i); render(); } }, 200);
    }
  }, [mode]);

  const goBack = () => {
    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
      return;
    }
    const from = location.state?.from;
    navigate(from && from.startsWith("/mssante") ? from : "/mssante/bienvenue", { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setServerErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pro/login`, { identifier, password });
      if (data?.twofa_required) {
        setTwofa({ challenge_token: data.challenge_token, ttl: data.challenge_ttl_sec || 300 });
      } else {
        setSession(data.token, data.user);
        goBack();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerErr(typeof detail === "string" ? detail : "Identifiants invalides.");
    } finally {
      setSubmitting(false);
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setServerErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pro/login/2fa/verify`, {
        challenge_token: twofa.challenge_token,
        code: twofaCode.trim(),
      });
      setSession(data.token, data.user);
      goBack();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerErr(typeof detail === "string" ? detail : "Code 2FA invalide.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setServerErr("");
    if (TURNSTILE_SITE_KEY && !tsToken) {
      setServerErr("Validez le contrôle anti-robot.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pro/login/code/request`, { identifier, turnstile_token: tsToken });
      setCodeSent(true);
      setInfo("Si un compte existe, un code vient d'être envoyé par email.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerErr(typeof detail === "string" ? detail : "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setServerErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pro/login/code/verify`, { identifier, code: code.trim() });
      if (data?.twofa_required) {
        setTwofa({ challenge_token: data.challenge_token, ttl: data.challenge_ttl_sec || 300 });
      } else {
        setSession(data.token, data.user);
        goBack();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerErr(typeof detail === "string" ? detail : "Code invalide.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #E0F2FE 100%)" }} data-testid="page-mssante-connexion">
      <SEO title="Connexion à votre espace sécurisé · MetaConnect"
           description="Accédez à votre espace professionnel MetaConnect."
           canonical="/mssante/connexion" noIndex />

      <section className="pt-24 pb-10 px-5 lg:px-8 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-8 items-start">

          {/* LEFT — Professional login */}
          <div>
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.18em] font-bold mb-3" style={{ background: "#E0F2FE", color: "#0369A1", border: "1px solid #BAE6FD" }}>
                <ShieldCheck size={11} /> Espace Professionnel
              </div>
              <h1 className="font-display text-[#0F2B5B] text-[28px] sm:text-[32px] font-semibold leading-tight">Connexion sécurisée</h1>
              <p className="mt-1.5 text-[13.5px] text-slate-600">Médecin, IDE, sage-femme, opticien, audioprothésiste — tous les PS.</p>
            </div>

            <div className="rounded-2xl bg-white p-6 lg:p-7 shadow-xl border border-slate-200">
            {twofa ? (
              <form onSubmit={verify2FA} className="grid gap-4" data-testid="conn-2fa-form">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#4FC3F7] font-bold mb-2">
                    <ShieldCheck size={12} /> Vérification en deux étapes
                  </div>
                  <h2 className="font-display text-slate-900 text-[20px] font-semibold">Entrez votre code 2FA</h2>
                  <p className="text-[12.5px] text-slate-500 mt-1">
                    Ouvrez votre application d&apos;authentification (Google Authenticator, Authy…) et saisissez le code à 6 chiffres.
                    Vous pouvez aussi utiliser l&apos;un de vos codes de récupération.
                  </p>
                </div>
                <div>
                  <Lbl>Code à 6 chiffres ou code de récupération</Lbl>
                  <input data-testid="conn-2fa-input"
                         className="w-full rounded-lg border border-slate-300 px-3 py-2.5 font-mono tracking-[0.3em] text-center text-[18px] text-slate-900 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20"
                         maxLength={20} autoFocus
                         value={twofaCode} onChange={(e) => setTwofaCode(e.target.value.toUpperCase())} />
                </div>
                {serverErr && <ErrBox>{serverErr}</ErrBox>}
                <button type="submit" disabled={submitting || twofaCode.length < 6} data-testid="conn-2fa-submit"
                        className="w-full px-6 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 text-[#0F2B5B] shadow-lg hover:shadow-xl transition-shadow"
                        style={{ background: "#0F2B5B" }}>
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  Valider <ArrowRight size={14} />
                </button>
                <button type="button" onClick={() => { setTwofa(null); setTwofaCode(""); setServerErr(""); }}
                        data-testid="conn-2fa-cancel"
                        className="text-[12px] text-slate-500 hover:text-slate-700">
                  ← Annuler et revenir au login
                </button>
              </form>
            ) : (
            <>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-slate-100 mb-5">
              <ModeBtn active={mode === "password"} onClick={() => { setMode("password"); setServerErr(""); }} testid="conn-mode-pwd">
                <KeyRound size={13} /> Mot de passe
              </ModeBtn>
              <ModeBtn active={mode === "code"} onClick={() => { setMode("code"); setServerErr(""); }} testid="conn-mode-code">
                <Mail size={13} /> Code email
              </ModeBtn>
            </div>

            {mode === "password" && (
              <form onSubmit={submitPassword} className="grid gap-4">
                <div>
                  <Lbl>Identifiant ou email</Lbl>
                  <input data-testid="conn-pwd-id" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20" autoComplete="username"
                         value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="prenom.nom@exemple.fr" />
                </div>
                <div>
                  <Lbl>Mot de passe</Lbl>
                  <input data-testid="conn-pwd-pwd" type="password" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] text-slate-900 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20" autoComplete="current-password"
                         value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                {serverErr && <ErrBox>{serverErr}</ErrBox>}
                <button type="submit" disabled={submitting} data-testid="conn-pwd-submit"
                        className="w-full px-6 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 text-[#0F2B5B] shadow-lg hover:shadow-xl transition-shadow"
                        style={{ background: "#0F2B5B" }}>
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Connexion..." : "Se connecter"}
                  {!submitting && <ArrowRight size={14} />}
                </button>
              </form>
            )}

            {mode === "code" && !codeSent && (
              <form onSubmit={requestCode} className="grid gap-4">
                <div>
                  <Lbl>Identifiant ou email</Lbl>
                  <input data-testid="conn-code-id" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] text-slate-900 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20"
                         value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="prenom.nom@exemple.fr" />
                </div>
                {TURNSTILE_SITE_KEY && <div ref={tsRef} className="pt-1" />}
                {serverErr && <ErrBox>{serverErr}</ErrBox>}
                <button type="submit" disabled={submitting} data-testid="conn-code-request"
                        className="w-full px-6 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 text-[#0F2B5B] shadow-lg"
                        style={{ background: "#0F2B5B" }}>
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  Recevoir mon code <Mail size={14} />
                </button>
              </form>
            )}

            {mode === "code" && codeSent && (
              <form onSubmit={verifyCode} className="grid gap-4">
                {info && (
                  <div className="text-[12.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">{info}</div>
                )}
                <div>
                  <Lbl>Code reçu par email (6 chiffres)</Lbl>
                  <input data-testid="conn-code-input" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 font-mono tracking-[0.4em] text-center text-[18px] text-slate-900 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20"
                         maxLength={6} inputMode="numeric"
                         value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                </div>
                {serverErr && <ErrBox>{serverErr}</ErrBox>}
                <button type="submit" disabled={submitting} data-testid="conn-code-verify"
                        className="w-full px-6 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 text-[#0F2B5B] shadow-lg"
                        style={{ background: "#0F2B5B" }}>
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  Valider le code <ArrowRight size={14} />
                </button>
                <button type="button" onClick={() => { setCodeSent(false); setCode(""); setInfo(""); }}
                        className="text-[12px] text-slate-500 hover:text-slate-700">
                  Renvoyer un code
                </button>
              </form>
            )}

            <div className="mt-5 pt-4 border-t border-slate-200">
              <button disabled
                      data-testid="conn-psc"
                      className="w-full opacity-50 cursor-not-allowed inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-medium border border-slate-300 text-slate-500">
                <ExternalLink size={13} /> Pro Santé Connect — Bientôt disponible
              </button>
            </div>

            <div className="mt-4 flex items-center justify-between text-[12px]">
              <Link to="/mssante/inscription" className="text-[#0F2B5B] font-semibold hover:underline">Créer un compte</Link>
              <a href="mailto:contact@metaconnect.fr?subject=Mot%20de%20passe%20oubli%C3%A9"
                 className="text-slate-500 hover:text-slate-700">Identifiant ou mot de passe oublié</a>
            </div>
            </>
            )}
            </div>
          </div>

          {/* RIGHT — Patient panel with phone mockup + FranceConnect+ */}
          <div className="hidden lg:block">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.18em] font-bold mb-3" style={{ background: "#D1FAE5", color: "#047857", border: "1px solid #6EE7B7" }}>
                <Mail size={11} /> Espace Patient
              </div>
              <h2 className="font-display text-[#0F2B5B] text-[24px] font-semibold leading-tight">Vos documents médicaux<br />dans votre poche</h2>
              <p className="mt-1.5 text-[13.5px] text-slate-600">Accédez à vos ordonnances, comptes-rendus et rendez-vous depuis votre téléphone.</p>
            </div>

            {/* Phone mockup */}
            <div className="relative flex justify-center">
              <div className="relative w-[260px] h-[520px] bg-slate-900 rounded-[40px] p-2 shadow-[0_30px_80px_rgba(0,0,0,0.5)] border-4 border-slate-800">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-full z-10" />
                <div className="w-full h-full rounded-[32px] bg-gradient-to-b from-[#F0F9FF] to-white overflow-hidden flex flex-col">
                  {/* Phone header */}
                  <div className="px-4 pt-7 pb-3 bg-[#0F2B5B] text-white">
                    <div className="flex items-center justify-between text-[10px] mb-2 opacity-70">
                      <span>14:32</span><span>●●●● 5G ▮</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-white/20 grid place-items-center text-[11px] font-bold">M</div>
                      <div>
                        <div className="text-[9px] opacity-80 leading-none">Mon Espace Santé</div>
                        <div className="text-[12px] font-bold leading-tight">Connectez-vous</div>
                      </div>
                    </div>
                  </div>
                  {/* FranceConnect+ button */}
                  <div className="flex-1 px-4 py-6 flex flex-col items-center justify-center text-center">
                    <ShieldCheck size={36} className="text-[#0F2B5B] mb-3" />
                    <div className="text-[12px] font-semibold text-slate-900">Identité vérifiée</div>
                    <div className="text-[10.5px] text-slate-500 mt-1 leading-snug">par le service<br />de l&apos;État français</div>

                    <button
                      data-testid="patient-franceconnect-btn"
                      onClick={(e) => { e.preventDefault(); }}
                      className="mt-5 w-full text-[#0F2B5B] py-3 rounded-xl text-[12px] font-bold inline-flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition-all"
                      style={{ background: "linear-gradient(135deg, #034EA2, #0066FF)" }}
                    >
                      <div className="w-5 h-5 rounded-full bg-white grid place-items-center text-[#034EA2] text-[9px] font-black">F+</div>
                      <span>S&apos;identifier avec<br/><span className="text-[14px]">FranceConnect+</span></span>
                    </button>

                    <div className="mt-3 text-[10px] text-slate-400">Niveau substantiel · eIDAS</div>

                    <div className="mt-4 w-full text-left text-[10.5px] text-slate-500 space-y-1.5">
                      <div className="flex items-center gap-1.5"><CheckIcon /> Identité numérique La Poste</div>
                      <div className="flex items-center gap-1.5"><CheckIcon /> Impôts gouv</div>
                      <div className="flex items-center gap-1.5"><CheckIcon /> Ameli · MSA</div>
                    </div>
                  </div>
                  {/* Tabbar */}
                  <div className="border-t border-slate-200 bg-white px-2 py-2 grid grid-cols-4 text-center text-[9px]">
                    {["Accueil", "Docs", "RDV", "Profil"].map((k, i) => (
                      <div key={i} className={i === 0 ? "text-[#0F2B5B] font-bold" : "text-slate-400"}>{k}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[10.5px] text-slate-600">
              <div className="rounded-lg bg-white border border-slate-200 p-2.5 shadow-sm">
                <Mail size={14} className="mx-auto mb-1 text-[#0EA5E9]" />Ordonnances
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2.5 shadow-sm">
                <ShieldCheck size={14} className="mx-auto mb-1 text-[#0EA5E9]" />Comptes-rendus
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2.5 shadow-sm">
                <KeyRound size={14} className="mx-auto mb-1 text-[#0EA5E9]" />Messagerie
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

function CheckIcon() {
  return <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />;
}

function Lbl({ children }) { return <span className="block text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">{children}</span>; }
function ErrBox({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
      <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{children}</span>
    </div>
  );
}
function ModeBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition inline-flex items-center justify-center gap-1.5 ${
              active ? "bg-[#0F2B5B] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>{children}</button>
  );
}
